import { KeyboardEvent, memo, useEffect, useRef, useState } from 'react';
import dayjs from '../../lib/dayjs';
import clsx from 'clsx';
import { Menu } from '@mantine/core';
import { useLazyPopover } from '../../molecules/PopoverAnchor';
import { usePortalTarget } from '../../provider/UiContext';
import { DTB_POPUP_Z } from '../popupZIndex';
import { HiCheck, HiChevronDown, HiOutlineMagnifyingGlass, HiXMark } from 'react-icons/hi2';
import MaskedDateInput from '../../atoms/MaskedDateInput/MaskedDateInput';
import { ValueType } from '../../types/ValueType';
import DataTableDateTimeFilter from './DataTableDateTimeFilter/DataTableDateTimeFilter';
import DataTableRangeFilter from './DataTableRangeFilter/DataTableRangeFilter';
import { DataTableFilterKind, DataTableFilterOperation, IDataTableFilterValue } from '../DataTable.types';
import { OP_LABEL, OP_SYMBOL, defaultFilterOpOf, filterOpsOf, hasTimePart } from '../dataTableFilters';

interface DataTableFilterCellProps {
    /** Поле колонки: уходит в onChange, чтобы колбэк владельца был стабильным. */
    columnId: string;
    kind: DataTableFilterKind;
    dataType?: ValueType;
    header: string;
    value?: IDataTableFilterValue;
    /** Доступные операции колонки (IDataTableColumn.filterOperations); без них — весь набор вида. */
    operations?: readonly DataTableFilterOperation[];
    /** Операция нового условия (IDataTableColumn.defaultFilterOperation). */
    defaultOperation?: DataTableFilterOperation;
    onChange: (columnId: string, value: IDataTableFilterValue | null) => void;
    /** Enter в поле: набранное отдаётся без дебаунса, и таблица применяет черновик фильтров. */
    onSubmit?: () => void;
}

const isBetween = (op: string) => op === 'between';

/** Редактор фильтра колонки (как DevExtreme filterRow): поле + кнопка-лупа выбора
 *  операции; для `between` — пара полей «с»/«по». Дата — MaskedDateInput,
 *  дата со временем — DataTableDateTimeFilter; значение фильтра всегда ISO. */
const DataTableFilterCell = memo((props: DataTableFilterCellProps) => {
    const { columnId, kind, dataType, header, value, operations, defaultOperation, onChange, onSubmit } = props;
    const ops = filterOpsOf(kind, operations);
    const defaultOp = defaultFilterOpOf(ops, defaultOperation);
    const hasOps = ops.length > 1 && kind !== 'boolean';
    const portalTarget = usePortalTarget();
    const [opMenuOpened, setOpMenuOpened] = useState(false);
    const opMenuPopover = useLazyPopover({ opened: opMenuOpened, haspopup: 'menu' });
    const [op, setOp] = useState<string>(value?.op ?? defaultOp);
    // between хранит пару [с, по] — восстановленный из сохранённого состояния
    // фильтр обязан заполнить оба поля, иначе следующий emit потеряет границу
    const [val, setVal] = useState<string>(
        value == null ? '' : Array.isArray(value.value) ? value.value[0] : String(value.value),
    );
    const [val2, setVal2] = useState<string>(Array.isArray(value?.value) ? value.value[1] : '');
    const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const pendingFire = useRef<(() => void) | undefined>(undefined);
    // Последнее значение, отправленное самой ячейкой: отличить своё эхо от условия, заданного
    // извне (ручка таблицы, сохранённый вид), — только последнее переписывает поля.
    const emittedRef = useRef<IDataTableFilterValue | null | undefined>(value);

    // Текст — с дебаунсом; выбор операции/число/дата из календаря — сразу.
    const emit = (nextOp: string, nextVal: string, nextVal2: string, immediate: boolean) => {
        if (debounce.current) clearTimeout(debounce.current);
        const fire = () => {
            const empty = isBetween(nextOp) ? nextVal === '' && nextVal2 === '' : nextVal === '';
            const next: IDataTableFilterValue | null = empty
                ? null
                : { op: nextOp, value: isBetween(nextOp) ? [nextVal, nextVal2] : nextVal };
            emittedRef.current = next;
            onChange(columnId, next);
        };
        pendingFire.current = undefined;
        if (immediate) fire();
        else {
            pendingFire.current = fire;
            debounce.current = setTimeout(() => {
                pendingFire.current = undefined;
                fire();
            }, 350);
        }
    };

    const submitOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter' || !onSubmit) return;
        e.preventDefault();
        if (debounce.current) clearTimeout(debounce.current);
        pendingFire.current?.();
        pendingFire.current = undefined;
        onSubmit();
    };

    useEffect(
        () => () => {
            if (debounce.current) clearTimeout(debounce.current);
        },
        [],
    );

    // Внешний сброс (value=undefined) не перемонтирует ячейку — синхронизируем вручную.
    // Печать пользователя не перетирается: тогда value остаётся непустым.
    useEffect(() => {
        if (value == null) {
            emittedRef.current = value;
            setVal('');
            setVal2('');
            setOp(defaultOp);
            return;
        }
        if (JSON.stringify(value) === JSON.stringify(emittedRef.current)) return;
        emittedRef.current = value;
        setOp(value.op);
        setVal(Array.isArray(value.value) ? value.value[0] : String(value.value));
        setVal2(Array.isArray(value.value) ? value.value[1] : '');
    }, [value, defaultOp]);

    const onOp = (nextOp: string) => {
        setOp(nextOp);
        // При уходе с between второй операнд теряет смысл — чистим, чтобы не «залип».
        const nextVal2 = isBetween(nextOp) ? val2 : '';
        if (!isBetween(nextOp)) setVal2('');
        emit(nextOp, val, nextVal2, true);
    };
    const onVal = (nextVal: string, immediate: boolean) => {
        setVal(nextVal);
        emit(op, nextVal, val2, immediate);
    };
    const onVal2 = (nextVal2: string, immediate: boolean) => {
        setVal2(nextVal2);
        emit(op, val, nextVal2, immediate);
    };
    const onReset = () => {
        setVal('');
        setVal2('');
        emit(op, '', '', true);
    };

    if (kind === 'boolean') {
        return (
            <div className="dtb__finput dtb__finput--boolean">
                <div className="dtb__ffield">
                    <select
                        className="dtb__finput-sel"
                        value={val}
                        onChange={(e) => onVal(e.target.value, true)}
                        aria-label={`Фильтр: ${header}`}
                    >
                        <option value="">Все</option>
                        <option value="true">Да</option>
                        <option value="false">Нет</option>
                    </select>
                    <HiChevronDown className="dtb__fselect-chevron" aria-hidden focusable={false} />
                </div>
            </div>
        );
    }

    // Тип фильтра показывает сама иконка: у «содержит» — лупа, у остальных
    // операций — их символ. Бейдж поверх лупы читался хуже и мельчил.
    const opSymbol = op === 'contains' ? null : OP_SYMBOL[op];

    // Menu — с первого открытия: лупа есть в каждой колонке строки фильтров; переключение — то, что иначе кладёт
    // Menu.Target.
    const opAnchor = hasOps && (
        <button
            type="button"
            {...opMenuPopover.anchorProps}
            className="dtb__floupe"
            aria-label="Операция фильтра"
            data-expanded={opMenuOpened || undefined}
            title={`Операция: ${OP_LABEL[op] ?? op}`}
            onClick={() => setOpMenuOpened((v) => !v)}
        >
            {opSymbol ? (
                <span className="dtb__fop-sym" aria-hidden>
                    {opSymbol}
                </span>
            ) : (
                <HiOutlineMagnifyingGlass aria-hidden />
            )}
        </button>
    );
    const opMenu = hasOps && opMenuPopover.mounted && (
        <Menu
            {...opMenuPopover.popoverProps}
            onChange={setOpMenuOpened}
            position="bottom-start"
            offset={6}
            zIndex={DTB_POPUP_Z}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
            classNames={{ dropdown: 'dtb-menu' }}
        >
            {opMenuPopover.popoverTarget}
            <Menu.Dropdown>
                {ops.map((o) => (
                    <Menu.Item
                        key={o}
                        className={clsx('dtb-menu__btn', o === op && 'is-active')}
                        rightSection={o === op ? <HiCheck className="dtb__fop-check" aria-hidden /> : undefined}
                        onClick={() => onOp(o)}
                    >
                        {OP_LABEL[o] ?? o}
                    </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item className="dtb-menu__btn" disabled={val === '' && val2 === ''} onClick={onReset}>
                    Сбросить
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );

    if (kind === 'date') {
        // Граница с точностью до времени пришла из кода или адреса («последние 3 часа»): поле дня её не
        // покажет, а маска при монтировании отдала бы эхом дату без времени и округлила условие до дня.
        // Такая граница показывается как есть, менять её можно только сбросом.
        const momentField = (
            v: string,
            onV: (next: string, immediate: boolean) => void,
            label: string,
            loupe: boolean,
        ) => (
            <span className="dtb__feditor">
                <input
                    className={clsx('dtb__finput-field', loupe && 'has-loupe', 'has-clear')}
                    type="text"
                    readOnly
                    value={dayjs(v).format('DD.MM.YYYY HH:mm')}
                    title="Условие с точностью до времени. Сбросьте его, чтобы выбрать дату"
                    aria-label={label}
                />
                <button
                    type="button"
                    className="dtb__fclear"
                    aria-label={`Очистить ${label.toLocaleLowerCase('ru-RU')}`}
                    title="Очистить фильтр"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onV('', true)}
                >
                    <HiXMark aria-hidden />
                </button>
            </span>
        );
        const dateField = (
            v: string,
            onV: (next: string, immediate: boolean) => void,
            label: string,
            ph: string,
            loupe: boolean,
        ) =>
            dataType === ValueType.DateTime || dataType === ValueType.NoSecDateTime ? (
                <DataTableDateTimeFilter
                    value={v}
                    onChange={(next) => onV(next, true)}
                    onSubmit={onSubmit}
                    label={label}
                    placeholder={ph}
                    withSeconds={dataType === ValueType.DateTime}
                    loupe={loupe}
                    withinPortal={!isBetween(op)}
                />
            ) : hasTimePart(v) ? (
                momentField(v, onV, label, loupe)
            ) : (
                <MaskedDateInput
                    value={v || null}
                    onChange={(iso) => onV(iso ?? '', true)}
                    onKeyDown={submitOnEnter}
                    placeholder={ph}
                    calendarPosition="right"
                    className={loupe ? 'dtb__fdate-loupe' : undefined}
                    aria-label={label}
                    compactActionsWhenClear
                    popoverProps={isBetween(op) ? { withinPortal: false } : undefined}
                    clearButtonProps={{
                        className: 'dtb__fclear',
                        'aria-label': `Очистить ${label.toLocaleLowerCase('ru-RU')}`,
                        title: 'Очистить фильтр',
                    }}
                />
            );
        // Лупа-операция лежит поверх поля слева (как у остальных типов),
        // календарь — правая секция поля
        return (
            <div className="dtb__finput dtb__finput--date">
                <div className="dtb__ffield dtb__ffield--date">
                    {opAnchor}
                    {opMenu}
                    {isBetween(op) ? (
                        <DataTableRangeFilter
                            header={header}
                            kind={kind}
                            from={val}
                            to={val2}
                            loupe={hasOps}
                            onReset={onReset}
                            fromEditor={dateField(val, onVal, `Фильтр: ${header}, с`, 'От', false)}
                            toEditor={dateField(val2, onVal2, `Фильтр: ${header}, по`, 'До', false)}
                        />
                    ) : (
                        dateField(val, onVal, `Фильтр: ${header}`, 'Фильтр', hasOps)
                    )}
                </div>
            </div>
        );
    }

    const inputType = kind === 'number' ? 'number' : 'text';
    const immediateInput = kind === 'number';

    const editor = (
        v: string,
        onV: (next: string, immediate: boolean) => void,
        label: string,
        ph: string,
        loupe: boolean,
    ) => (
        <span className="dtb__feditor">
            <input
                className={clsx('dtb__finput-field', loupe && 'has-loupe', v !== '' && 'has-clear')}
                type={inputType}
                value={v}
                placeholder={ph}
                onChange={(e) => onV(e.target.value, immediateInput)}
                onKeyDown={submitOnEnter}
                aria-label={label}
            />
            {v !== '' && (
                <button
                    type="button"
                    className="dtb__fclear"
                    aria-label={`Очистить ${label.toLocaleLowerCase('ru-RU')}`}
                    title="Очистить фильтр"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onV('', true)}
                >
                    <HiXMark aria-hidden />
                </button>
            )}
        </span>
    );

    return (
        <div className={`dtb__finput dtb__finput--${kind}`}>
            <div className="dtb__ffield">
                {opAnchor}
                {opMenu}
                {isBetween(op) ? (
                    <DataTableRangeFilter
                        header={header}
                        kind={kind}
                        from={val}
                        to={val2}
                        loupe={hasOps}
                        onReset={onReset}
                        fromEditor={editor(val, onVal, `Фильтр: ${header}, с`, 'От', false)}
                        toEditor={editor(val2, onVal2, `Фильтр: ${header}, по`, 'До', false)}
                    />
                ) : (
                    editor(val, onVal, `Фильтр: ${header}`, 'Фильтр', hasOps)
                )}
            </div>
        </div>
    );
});
DataTableFilterCell.displayName = 'DataTableFilterCell';

export default DataTableFilterCell;
